/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

import { useCallback, useState } from 'react';

import Message from 'experiments/ai-features/bpmn-copilot/Message';
import { UseValueButton } from 'experiments/ai-features/feel-copilot/FeelMessages.styled';
import { FeelMessageCodeSnippet } from 'experiments/ai-features/feel-copilot/FeelMessageCodeSnippet';

export function FeelMessages(props) {
  const { message, onUseExpressionClick, onUseContextClick } = props;

  const [showContextMap, setShowContextMap] = useState({});

  const isContextNonEmpty = useCallback((message) => {
    return message?.context && Object.keys(message.context)?.length > 0;
  }, []);

  const handleContextButtonClick = useCallback(
    (message) => {
      if (!showContextMap[message.id]) {
        setShowContextMap({ ...showContextMap, [message.id]: true });
      } else {
        onUseContextClick(message.context);
      }
    },
    [showContextMap, onUseContextClick]
  );

  if (message.feel && isContextNonEmpty(message)) {
    // FEEL expression and context both present - show both
    return (
      <>
        <Message key={`${message.id}-feel`} type={message.type} useCodeStyle>
          <FeelMessageCodeSnippet>{message.feel}</FeelMessageCodeSnippet>
        </Message>

        <UseValueButton
          kind="ghost"
          size="sm"
          key={`${message.id}-feel-use`}
          onClick={() => onUseExpressionClick(message.feel)}
        >
          Use expression
        </UseValueButton>

        <UseValueButton
          kind="ghost"
          size="sm"
          key={`${message.id}-show-context`}
          onClick={() => handleContextButtonClick(message)}
        >
          {showContextMap[message.id] ? 'Use' : 'Show'} context
        </UseValueButton>

        {showContextMap[message.id] && (
          <Message key={`${message.id}-context`} type={message.type} useCodeStyle>
            <FeelMessageCodeSnippet>{JSON.stringify(message.context, null, 2)}</FeelMessageCodeSnippet>
          </Message>
        )}
      </>
    );
  } else if (message.feel) {
    // FEEL expression only
    return (
      <>
        <Message key={`${message.id}-feel`} type={message.type} useCodeStyle>
          <FeelMessageCodeSnippet>{message.feel}</FeelMessageCodeSnippet>
        </Message>

        <UseValueButton
          kind="ghost"
          size="sm"
          key={`${message.id}-feel-use`}
          onClick={() => onUseExpressionClick(message.feel, message.id)}
        >
          Use expression
        </UseValueButton>
      </>
    );
  } else if (isContextNonEmpty(message)) {
    // Context only
    return (
      <>
        <Message key={`${message.id}-context`} type={message.type} useCodeStyle>
          <FeelMessageCodeSnippet>{JSON.stringify(message.context, null, 2)}</FeelMessageCodeSnippet>
        </Message>

        <UseValueButton
          kind="ghost"
          size="sm"
          key={`${message.id}-show-context`}
          onClick={() => onUseContextClick(message.context)}
        >
          Use context
        </UseValueButton>
      </>
    );
  } else {
    // No expression or context - don't return anything from this component
    return null;
  }
}
